package com.rajen.angrejidictionary.components.sections

import androidx.compose.runtime.Composable
import com.rajen.angrejidictionary.analytics.Umami
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val TitleColor = Color.rgb(0xD3, 0x35, 0x3E)

val FooterStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(Color.rgb(0xFF, 0xEC, 0xDE))
        .padding(topBottom = 0.8.cssRem, leftRight = 5.percent)
        .borderTop(1.px, LineStyle.Solid, Color.rgb(0x21, 0x00, 0x00))
}

val FooterRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(1.25.cssRem)
            .flexDirection(FlexDirection.Column)
            .alignItems(AlignItems.Center)
    }
    Breakpoint.MD {
        Modifier
            .flexDirection(FlexDirection.Row)
            .justifyContent(JustifyContent.SpaceBetween)
            .alignItems(AlignItems.Center)
    }
}

val SocialLinksStyle = CssStyle.base {
    Modifier.gap(1.5.cssRem).flexWrap(FlexWrap.Wrap)
}

@OptIn(ExperimentalComposeWebApi::class)
val FooterLinkStyle = CssStyle {
    base {
        Modifier
            .color(Color.rgb(0x21, 0x00, 0x00))
            .fontSize(0.95.cssRem)
            .textDecorationLine(TextDecorationLine.None)
            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
            .transition {
                property("color")
                duration(0.2.s)
                timingFunction(AnimationTimingFunction.Ease)
            }
    }
    hover {
        Modifier.color(Color.rgb(0x45, 0xA4, 0xB8))
    }
}

val CopyrightStyle = CssStyle.base {
    Modifier
        .color(Color.rgb(0x21, 0x00, 0x00))
        .fontSize(0.9.cssRem)
        .opacity(0.85)
        .textAlign(TextAlign.Center)
}

@OptIn(ExperimentalComposeWebApi::class)
val FooterLinkActiveStyle = CssStyle.base {
    Modifier
        .color(TitleColor)
        .fontSize(0.95.cssRem)
        .fontWeight(FontWeight.Bold)
        .textDecorationLine(TextDecorationLine.Underline)
        .textUnderlineOffset(4.px)
        .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
}

@Composable
fun Footer(modifier: Modifier = Modifier) {
    val ctx = rememberPageContext()
    val currentPath = ctx.route.path

    Column(
        FooterStyle.toModifier().then(modifier),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Row(
            FooterRowStyle.toModifier(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Row(SocialLinksStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
                SocialIcon(SocialLinks.TWITTER_X, "x") { FaXTwitter() }
                SocialIcon(SocialLinks.INSTAGRAM, "instagram") { FaInstagram() }
                SocialIcon(SocialLinks.FACEBOOK, "facebook") { FaFacebookF() }
                SocialIcon(SocialLinks.LINKED_IN, "linkedin") { FaLinkedinIn() }
                Box(Modifier.width(1.px).height(16.px).backgroundColor(Color.rgba(33, 0, 0, 0.2f)).margin(leftRight = 4.px))

                SocialIcon(SocialLinks.PLAY_STORE, "play-store") { FaGooglePlay() }
            }

            Row(
                Modifier.gap(0.75.cssRem).displayIfAtLeast(Breakpoint.MD),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FooterNavItem("Terms", "/terms", currentPath)
                TightDivider()
                FooterNavItem("Privacy", "/privacy", currentPath)
                TightDivider()
                FooterNavItem("Contact", "/contact", currentPath)
                SpanText(
                    "© 2026 Angreji Dictionary",
                    CopyrightStyle.toModifier().margin(left = 2.5.cssRem)
                )
            }
        }

        Column(
            Modifier.displayUntil(Breakpoint.MD).margin(top = 1.25.cssRem).gap(1.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                FooterNavItem("Terms", "/terms", currentPath)
                TightDivider()
                FooterNavItem("Privacy", "/privacy", currentPath)
                TightDivider()
                FooterNavItem("Contact", "/contact", currentPath)
            }
            SpanText("© 2026 Angreji Dictionary", CopyrightStyle.toModifier())
        }
    }
}

@Composable
private fun FooterNavItem(
    text: String,
    destination: String,
    currentPath: String
) {
    val isActive = currentPath == destination
    val style = if (isActive) FooterLinkActiveStyle else FooterLinkStyle

    Link(
        path = destination,
        modifier = style.toModifier().onClick { Umami.trackNavClick(destination, "footer") }
    ) {
        Text(text)
    }
}

// External links (social) — these stay as <a> tags
@Composable
private fun SocialIcon(href: String, platform: String, icon: @Composable () -> Unit) {
    org.jetbrains.compose.web.dom.A(
        href = href,
        attrs = FooterLinkStyle.toModifier().toAttrs {
            attr("target", "_blank")
            attr("rel", "noopener noreferrer")
            onClick { Umami.trackSocialClick(platform) }
        }
    ) {
        icon()
    }
}

@Composable
private fun TightDivider() {
    SpanText(
        "|",
        Modifier
            .color(Color.rgb(0x21, 0x00, 0x00))
            .margin(leftRight = 0.25.cssRem)
            .opacity(0.7)
    )
}